import { useEffect, useState, useRef, useCallback } from 'react';
import { useApi } from '../../../utils/hooks';
import { Helpers } from '../../../utils/helpers';
import { debounce } from 'lodash';
import { SortDirection } from '../../../utils/constants/enums';
import {
  IPagedResult,
  ISystemCodeDto,
  ISystemCodeSearchRequest
} from '../../../utils/constants/interfaces';

// Define an empty result set for when no data is returned
const emptySearchResults: IPagedResult<ISystemCodeDto[]> = {
  currentPage: 1,
  results: [],
  pageSize: 1,
  pageCount: 1,
  resultCount: 0
};

const useSystemCodeLookup = (
  requireKeywords: boolean,
  onChange: (systemCodeId: string) => void
) => {
  const defaultSearchParams: ISystemCodeSearchRequest = {
    keywords: '',
    sortDirection: SortDirection.ASCENDING,
    sortField: 'codeType',
    pageSize: 20,
    page: 1,
    filters: {
      systemCodeId: '',
      codeType: '',
      codeValue: '',
      description: '',
      info: ''
    }
  };

  const api = useApi();
  const [loadingCodeTypes, setLoadingCodeTypes] = useState<boolean>(false);
  const [loadingSearch, setLoadingSearch] = useState<boolean>(false);
  const [pageIsReady, setPageIsReady] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [systemCodePagedResults, setSystemCodePagedResults] =
    useState<IPagedResult<ISystemCodeDto[]>>(emptySearchResults);
  const [searchParameters, setSearchParameters] =
    useState<ISystemCodeSearchRequest>(defaultSearchParams);
  const [codeTypes, setCodeTypes] = useState<string[]>([]);

  const search = useCallback(async (): Promise<void> => {
    // Only prevent search if requireKeywords is true and no keywords provided
    if (requireKeywords && Helpers.isNullOrWhiteSpace(searchParameters.keywords)) {
      setSystemCodePagedResults(emptySearchResults);
      return;
    }

    setLoadingSearch(true);

    const filters = JSON.stringify(searchParameters.filters);
    const queryParams = [];
    queryParams.push(`page=${searchParameters.page}`);
    queryParams.push(`pageSize=${searchParameters.pageSize}`);
    queryParams.push(`sortField=${searchParameters.sortField}`);
    queryParams.push(`sortDirection=${searchParameters.sortDirection.toString()}`);
    queryParams.push(`keywords=${encodeURIComponent(searchParameters.keywords)}`);
    queryParams.push(`filters=${encodeURIComponent(filters)}`);

    const endpoint = `/codes?${queryParams.join('&')}`;
    const apiResponse = await api.get<IPagedResult<ISystemCodeDto[]>>(endpoint);
    if (apiResponse.isSuccessStatusCode && apiResponse.response) {
      setSystemCodePagedResults(apiResponse.response);
    } else {
      setSystemCodePagedResults(emptySearchResults);
    }
    setLoadingSearch(false);
  }, [api, searchParameters, requireKeywords]);

  const resetSearch = () => {
    setSearchParameters(defaultSearchParams);
  };

  const changePage = (page: number) => {
    setSearchParameters({ ...searchParameters, page: page });
  };

  const changeSort = (sortField: string) => {
    let sortDirection: SortDirection;
    if (searchParameters.sortField === sortField) {
      // Same field clicked - toggle direction
      sortDirection =
        (searchParameters.sortDirection as SortDirection) === SortDirection.ASCENDING
          ? SortDirection.DESCENDING
          : SortDirection.ASCENDING;
    } else {
      // Different field clicked - start with ascending
      sortDirection = SortDirection.ASCENDING;
    }

    setSearchParameters({
      ...searchParameters,
      sortField: sortField,
      sortDirection: sortDirection
    });
  };

  const selectSystemCode = (systemCodeId: string) => {
    setShowSearch(false);
    resetSearch();
    onChange(systemCodeId);
  };

  const handleCodeTypeChange = (codeType: string) => {
    const searchParams = { ...searchParameters };
    searchParams.filters.codeType = codeType;
    searchParams.page = 1; // Reset page if filtered
    setSearchParameters(searchParams);
  };

  const hasSearchParametersCheck = () => {
    if (requireKeywords && Helpers.isNullOrWhiteSpace(searchParameters.keywords)) return false;

    return (
      !Helpers.isNullOrWhiteSpace(searchParameters.keywords) ||
      !Helpers.isNullOrWhiteSpace(searchParameters.filters.codeType)
    );
  };

  const debouncedSystemCodeSearch = useRef(
    debounce((keywords: string) => {
      setSearchParameters({ ...searchParameters, page: 1, keywords: keywords });
    }, 300)
  ).current;

  useEffect(() => {
    return () => debouncedSystemCodeSearch.cancel();
  }, [debouncedSystemCodeSearch]);

  useEffect(() => {
    void search();
  }, [search, searchParameters]);

  useEffect(() => {
    const getCodeTypes = async () => {
      setLoadingCodeTypes(true);
      const apiResponse = await api.get<string[]>('/codes/code-types');
      if (apiResponse.isSuccessStatusCode) {
        setCodeTypes(apiResponse?.response ?? []);
      }
      setPageIsReady(true);
      setLoadingCodeTypes(false);
    };
    void getCodeTypes();
  }, [api]);

  return {
    pageIsReady,
    loadingCodeTypes,
    loadingSearch,
    searchParameters,
    codeTypes,
    systemCodePagedResults,
    debouncedSystemCodeSearch,
    showSearch,
    hasSearchParameters: hasSearchParametersCheck(),
    setShowSearch,
    selectSystemCode,
    changePage,
    changeSort,
    handleCodeTypeChange
  };
};

export default useSystemCodeLookup;
